/**
 * Routy's setup tools: reading and changing the business profile, company
 * hours and settings, offices, booking forms and their questions, weekly
 * availability and the team, the way the settings screens do. Every write
 * shows a confirm card first.
 *
 * Synced verbatim to the mobile app, so every import must exist at the same
 * path there; `../settings/setupEvents` is the one module each platform
 * implements itself.
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';
import { ApiError } from '../../api/core/ApiError';
import type { Routemize_DTOs_BookingFormDto } from '../../api/models/Routemize_DTOs_BookingFormDto';
import type { Routemize_DTOs_BookingFormQuestionDto } from '../../api/models/Routemize_DTOs_BookingFormQuestionDto';
import type { Routemize_DTOs_BookingFormSettingDto } from '../../api/models/Routemize_DTOs_BookingFormSettingDto';
import type { Routemize_DTOs_CompanyLocationDto } from '../../api/models/Routemize_DTOs_CompanyLocationDto';
import type { Routemize_DTOs_CompanySettingsDTO } from '../../api/models/Routemize_DTOs_CompanySettingsDTO';
import type { Routemize_DTOs_CompanyWorkScheduleDTO } from '../../api/models/Routemize_DTOs_CompanyWorkScheduleDTO';
import type { Routemize_DTOs_CreateUpdateCompanyLocationDto } from '../../api/models/Routemize_DTOs_CreateUpdateCompanyLocationDto';
import type { Routemize_DTOs_MasterData } from '../../api/models/Routemize_DTOs_MasterData';
import type { Routemize_DTOs_TeamMemberListDto } from '../../api/models/Routemize_DTOs_TeamMemberListDto';
import type { Routemize_DTOs_UserAddressDto } from '../../api/models/Routemize_DTOs_UserAddressDto';
import type { Routemize_DTOs_UserInviteDto } from '../../api/models/Routemize_DTOs_UserInviteDto';
import type { Routemize_DTOs_UserSmallDTO } from '../../api/models/Routemize_DTOs_UserSmallDTO';
import { Routemize_Constants_AddressType } from '../../api/models/Routemize_Constants_AddressType';
import { Routemize_Constants_BaseLocation } from '../../api/models/Routemize_Constants_BaseLocation';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import { Routemize_Constants_EventNature } from '../../api/models/Routemize_Constants_EventNature';
import { Routemize_Constants_InviteStatus } from '../../api/models/Routemize_Constants_InviteStatus';
import { BookingFormQuestionService } from '../../api/services/BookingFormQuestionService';
import { BookingFormService } from '../../api/services/BookingFormService';
import { BookingFormSettingService } from '../../api/services/BookingFormSettingService';
import { BusinessBrandingService } from '../../api/services/BusinessBrandingService';
import { CompanyLocationService } from '../../api/services/CompanyLocationService';
import { CompanySettingsService } from '../../api/services/CompanySettingsService';
import { CompanyWorkScheduleService } from '../../api/services/CompanyWorkScheduleService';
import { GoogleCalendarService } from '../../api/services/GoogleCalendarService';
import { MasterDataService } from '../../api/services/MasterDataService';
import { SubscriptionService } from '../../api/services/SubscriptionService';
import { UserService } from '../../api/services/UserService';
import { formatDurationMinutes, formatTimeString } from '../../utils/timeUtils';
import { userWorkScheduleManager } from '../availability/UserWorkScheduleManager';
import { getFeatureDefinition, tierLabel } from '../billing/featureCatalog';
import { STARTER_TEAM_MEMBER_ONLY_MESSAGE, isRoleAllowedOnPlan } from '../billing/starterSeats';
import { notifySetupChanged } from '../settings/setupEvents';
import { SYSTEM_ROLE_NAMES, UserRoleConfigManager } from '../users/UserRoleConfig';
import type { RoutySetupPage } from './routyArtifacts';
import {
  COMPANY_SETTING_SPECS,
  EVENT_COLORS,
  QUESTION_KINDS,
  SUPPORTED_TIME_ZONES,
  applyQuestionEdits,
  buildCompanySettingsPatch,
  buildFormDetailUpdate,
  buildFormSettingsUpdate,
  buildNewFormSettings,
  buildQuestionOutline,
  buildTemplateQuestions,
  cleanText,
  describeCompanySettings,
  describeFormSettings,
  describeFormTemplates,
  describeWeekSchedule,
  diffQuestions,
  flattenTemplateQuestions,
  formBookableDays,
  formatDayList,
  homeAddressUpsert,
  isRoleDowngrade,
  labelKey,
  matchBookingForms,
  matchRoleName,
  matchTeamMember,
  mergeWeekSchedule,
  nextAssigneeIds,
  orderQuestionsForBuilder,
  parseInvites,
  parseQuestionEdits,
  parseTemplateRequest,
  parseWeekday,
  planWeeklyAvailability,
  primaryRoleName,
  resolveBookingFormChanges,
  resolveBusinessProfile,
  resolveCompanySettings,
  resolveNewBookingForm,
  starterRoleAllowance,
  summarizeQuestions,
  type DayHoursChange,
  type QuestionEdit,
  type WeeklyHoursChange,
} from './routySetupHelpers';
import {
  fetchRawBookingForms,
  geocodeOrThrow,
  invalidateBookingFormCaches,
  parseFormattedAddress,
  type ConfirmLine,
  type RoutyToolDeps,
} from './routyTools';

type Input = Record<string, unknown>;
type TeamMember = Routemize_DTOs_TeamMemberListDto;
type Question = Routemize_DTOs_BookingFormQuestionDto;

const MAX_LISTED = 25;
const DEFAULT_EVENT_TYPE = 'On-Site Estimate';

const SETUP_PAGES: readonly RoutySetupPage[] = [
  'billing',
  'team',
  'integrations',
  'branding',
  'territories',
  'booking_forms',
  'form_editor',
];

// ─────────────────────────────────────────────────────────────────────────────
// Tool definitions
// ─────────────────────────────────────────────────────────────────────────────

const FORM_REFERENCE = {
  type: 'string',
  description: 'The form\'s id (from list_booking_forms with includeDrafts=true) or its name.',
} as const;

const FORM_SETTING_PROPERTIES = {
  name: { type: 'string', description: 'Form name the team sees, 1-100 characters.' },
  internalNote: { type: 'string', description: 'One line for the team on what the form is for, 1-500 characters.' },
  serviceLabel: {
    type: 'string',
    description: 'What customers book, shown on the booking page (e.g. "Free Estimate"), 1-50 characters.',
  },
  durationMinutes: { type: 'number', description: 'Visit length, 5-720 minutes in steps of 5.' },
  bufferMinutes: { type: 'number', description: 'Buffer after each visit, 0-240 minutes in steps of 5.' },
  bookableDays: {
    type: 'array',
    items: { type: 'string' },
    description: 'Weekdays customers can book, e.g. ["Monday", "Wednesday"]. Must be days the company is open.',
  },
  earliestStart: { type: 'string', description: 'Earliest appointment start, 24-hour "HH:mm".' },
  latestStart: { type: 'string', description: 'Latest appointment START, 24-hour "HH:mm" (not the end of the day).' },
  sameDayBooking: { type: 'boolean', description: 'Allow booking for today.' },
  sameDayCutoff: { type: 'string', description: 'Last time of day a same-day booking can be made, "HH:mm".' },
  minimumNoticeHours: { type: 'number', description: 'Hours of notice required, 0-168.' },
  maxDaysAhead: { type: 'number', description: 'How far out customers can book, 1-365 days.' },
  bookingMode: {
    type: 'string',
    enum: ['instant', 'request'],
    description: 'instant: customers book straight onto the calendar. request: bookings arrive as requests to approve.',
  },
  assignment: {
    type: 'string',
    enum: ['solo', 'round_robin'],
    description: 'round_robin (Smart Assignment) spreads bookings across 2+ assignees; solo books the assigned person.',
  },
  emailConfirmation: { type: 'boolean', description: 'Email the customer a confirmation.' },
  textConfirmation: { type: 'boolean', description: 'Text the customer a confirmation.' },
  emailReminders: { type: 'boolean', description: 'Email the customer reminders.' },
  textReminders: { type: 'boolean', description: 'Text the customer reminders.' },
  color: { type: 'string', enum: Object.keys(EVENT_COLORS), description: 'Calendar color for these appointments.' },
  showInDirectory: { type: 'boolean', description: 'List the form in the public Routemize directory.' },
  directoryCategory: { type: 'string', description: 'Directory trade, e.g. Painting, Roofing, Hvac, Other.' },
} as const;

const TEMPLATE_PROPERTY = {
  type: 'object',
  description:
    'Build questions from an industry template, the way the form builder\'s AI wizard does (see list_form_templates).',
  properties: {
    industry: { type: 'string', description: 'Template id or name, e.g. "painting".' },
    services: {
      type: 'array',
      items: { type: 'string' },
      description: 'The services the business offers, by template service id or label. Becomes the first question.',
    },
    goal: {
      type: 'string',
      enum: ['qualified', 'bookings'],
      description:
        'qualified (default): more questions, low-intent answers become requests. bookings: fewer questions, everything books.',
    },
    addOns: {
      type: 'array',
      items: { type: 'string' },
      description: 'Add-on question ids to include. Leave out for the wizard\'s defaults.',
    },
    skipFollowUpsFor: {
      type: 'array',
      items: { type: 'string' },
      description: 'Services that should not get their follow-up question.',
    },
    includePhotoPrompts: { type: 'boolean', description: 'Ask for photos after relevant answers (default on).' },
  },
  required: ['industry', 'services'],
} as const;

const ANSWER_CONDITION = {
  type: 'object',
  description: 'Show this question only after this answer on a pick_one question.',
  properties: {
    question: { type: 'string', description: 'The pick_one question: id, exact label, or ref.' },
    answer: { type: 'string', description: 'The answer that reveals it, exactly as written.' },
  },
  required: ['question', 'answer'],
} as const;

const QUESTION_EDIT = {
  type: 'object',
  properties: {
    action: {
      type: 'string',
      enum: ['add', 'update', 'remove', 'move', 'show_when', 'always_show'],
      description:
        'add a question; update its label, kind, answers or flags; remove it; move it; show_when makes it a ' +
        'follow-up of one answer; always_show turns a follow-up back into a question everyone sees.',
    },
    question: { type: 'string', description: 'The question to change (not for add): id, exact label, or ref.' },
    ref: { type: 'string', description: 'add only: a short name to refer to this new question later in the same call.' },
    label: { type: 'string', description: 'The question text.' },
    kind: { type: 'string', enum: QUESTION_KINDS },
    required: { type: 'boolean' },
    answers: {
      type: 'array',
      items: { type: 'string' },
      description: 'Choice answers (dropdown, pick_one, pick_any). On update this replaces the list; prefer addAnswers/removeAnswers/renameAnswers.',
    },
    addAnswers: { type: 'array', items: { type: 'string' } },
    removeAnswers: { type: 'array', items: { type: 'string' } },
    renameAnswers: {
      type: 'array',
      items: {
        type: 'object',
        properties: { from: { type: 'string' }, to: { type: 'string' } },
        required: ['from', 'to'],
      },
      description: 'Rename answers in place, keeping their follow-ups and flags.',
    },
    requestOnlyAnswers: {
      type: 'array',
      items: { type: 'string' },
      description: 'Answers that turn the booking into a request to approve instead of an instant booking.',
    },
    bookableAnswers: {
      type: 'array',
      items: { type: 'string' },
      description: 'update only: answers that should book normally again.',
    },
    after: { type: 'string', description: 'add/move: place it right after this question.' },
    position: { type: 'number', description: 'add/move: 1-based position in the list.' },
    showWhen: ANSWER_CONDITION,
  },
  required: ['action'],
} as const;

export const SETUP_TOOLS: Tool[] = [
  {
    name: 'get_setup_overview',
    description:
      'Read the account setup in one call: plan and locked features, business profile, company hours, company ' +
      'settings (keyed like update_company_settings), service area, offices, booking forms (status, default link), ' +
      'calendar and text-messaging connections, and for admins the team and pending invites. Use for broad ' +
      'setup questions ("what\'s left to set up?") and before changing several things at once.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'get_booking_form',
    description:
      'Read one booking form in full before changing it: settings in plain language (keyed like update_booking_form), ' +
      'assignees, status, public link, lead source, and every question with its kind, answers, follow-ups (shownWhen) ' +
      'and request-only answers. Drafts and lead-source forms included.',
    input_schema: { type: 'object', properties: { form: FORM_REFERENCE }, required: ['form'] },
  },
  {
    name: 'list_form_templates',
    description:
      'List the industry question templates the form builder\'s AI wizard uses. With industry: that template\'s ' +
      'services (pick from these for template.services), which services have follow-ups, and its add-on questions.',
    input_schema: {
      type: 'object',
      properties: { industry: { type: 'string', description: 'Template id or name, e.g. "painting".' } },
    },
  },
  {
    name: 'create_booking_form',
    description:
      'Create a booking form (a draft unless publish=true). name, serviceLabel and internalNote are required; ' +
      'everything else starts at the form editor\'s defaults and the booking window is kept inside company hours. ' +
      'template adds industry questions like the AI wizard. assignees defaults to the operator. Shows a confirm card.',
    input_schema: {
      type: 'object',
      properties: {
        ...FORM_SETTING_PROPERTIES,
        assignees: {
          type: 'array',
          items: { type: 'string' },
          description: 'Team members who take these bookings (names or emails). Defaults to the operator.',
        },
        eventType: { type: 'string', description: 'Appointment type, e.g. "On-Site Estimate" (the default).' },
        leadSource: {
          type: 'string',
          description: 'Tag every booking with this lead source, e.g. "Facebook". Leave out for internal forms.',
        },
        template: TEMPLATE_PROPERTY,
        publish: { type: 'boolean', description: 'Publish right away so it can take bookings.' },
      },
      required: ['name', 'serviceLabel', 'internalNote'],
    },
  },
  {
    name: 'update_booking_form',
    description:
      'Change one booking form\'s settings, assignees or status. Pass only what changes. status publishes a draft, ' +
      'unpublishes, or archives. makeDefaultLink makes it the company\'s main booking link. Shows a confirm card ' +
      'with each change before and after.',
    input_schema: {
      type: 'object',
      properties: {
        form: FORM_REFERENCE,
        ...FORM_SETTING_PROPERTIES,
        addAssignees: { type: 'array', items: { type: 'string' }, description: 'Team members to add (names or emails).' },
        removeAssignees: { type: 'array', items: { type: 'string' }, description: 'Team members to remove.' },
        status: { type: 'string', enum: ['publish', 'unpublish', 'archive'] },
        makeDefaultLink: { type: 'boolean', description: 'true makes this the default booking link.' },
      },
      required: ['form'],
    },
  },
  {
    name: 'edit_form_questions',
    description:
      'Add, change, remove, reorder or branch the questions on one booking form, and/or add an industry template\'s ' +
      'questions. Edits run in order and refer to questions by id, exact label, or the ref of one added earlier in ' +
      'the same call. Follow-ups (showWhen) and request-only answers need Form Pre-qualification. Shows the whole ' +
      'question list for confirmation. Call get_booking_form first.',
    input_schema: {
      type: 'object',
      properties: {
        form: FORM_REFERENCE,
        edits: { type: 'array', items: QUESTION_EDIT },
        template: TEMPLATE_PROPERTY,
        replaceExisting: {
          type: 'boolean',
          description: 'Remove every existing question first (only when they ask to start over).',
        },
      },
      required: ['form'],
    },
  },
  {
    name: 'update_business_profile',
    description:
      'Change the business name, website, email, phone, time zone, country or brand colors. Logos are uploaded on the ' +
      'branding page (open_setup_page).',
    input_schema: {
      type: 'object',
      properties: {
        businessName: { type: 'string' },
        website: { type: 'string' },
        email: { type: 'string', description: 'The business email customers see.' },
        phone: { type: 'string', description: 'Full phone number with area code.' },
        timeZone: { type: 'string', enum: Object.keys(SUPPORTED_TIME_ZONES) },
        country: { type: 'string', enum: ['US', 'CA'] },
        primaryColor: { type: 'string', description: 'Hex color, e.g. #1F6FEB.' },
        secondaryColor: { type: 'string' },
        accentColor: { type: 'string' },
      },
    },
  },
  {
    name: 'update_company_hours',
    description:
      'Change the company\'s weekly open hours and lunch break. List only the days that change. These are the ' +
      'hours customers can book within; a person\'s own hours are update_weekly_availability.',
    input_schema: {
      type: 'object',
      properties: {
        days: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              day: { type: 'string', description: 'Weekday, e.g. "Saturday".' },
              open: { type: 'boolean', description: 'false closes the day; true opens a closed one.' },
              start: { type: 'string', description: 'Opening time, "HH:mm".' },
              end: { type: 'string', description: 'Closing time, "HH:mm".' },
              lunchStart: { type: ['string', 'null'], description: '"HH:mm", or null to remove the lunch break.' },
              lunchEnd: { type: ['string', 'null'] },
            },
            required: ['day'],
          },
        },
      },
      required: ['days'],
    },
  },
  {
    name: 'update_company_settings',
    description:
      'Change company-wide scheduling, routing, request and reminder settings. Pass only the keys that change; ' +
      'get_setup_overview shows the current values under settings.',
    input_schema: {
      type: 'object',
      properties: Object.fromEntries(COMPANY_SETTING_SPECS.map(spec => [spec.key, spec.schema])),
    },
  },
  {
    name: 'save_address',
    description:
      'Save the company office address (kind office: where routes can start and end) or the operator\'s own home ' +
      'address (kind home). The address is looked up on the map first. Shows a confirm card.',
    input_schema: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['office', 'home'] },
        address: { type: 'string', description: 'Full street address with city, state and ZIP.' },
        unit: { type: 'string', description: 'Suite or unit, if any.' },
        locationName: { type: 'string', description: 'office only: e.g. "Main Office".' },
        addAsNew: { type: 'boolean', description: 'office only: add another location instead of moving the main one.' },
        makeDefault: { type: 'boolean', description: 'office only, with addAsNew: make the new one the main office.' },
      },
      required: ['kind', 'address'],
    },
  },
  {
    name: 'update_weekly_availability',
    description:
      'Set someone\'s own weekly working hours on days the company is open, or put a day back on company hours. ' +
      'Changes every week going forward; for specific dates off use block_days_off. person defaults to the operator.',
    input_schema: {
      type: 'object',
      properties: {
        person: { type: 'string', description: 'Team member name or email (managers only). Leave out for the operator.' },
        days: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              day: { type: 'string', description: 'Weekday, e.g. "Friday".' },
              start: { type: 'string', description: '"HH:mm".' },
              end: { type: 'string', description: '"HH:mm".' },
              useCompanyHours: { type: 'boolean', description: 'true drops their own hours for the day.' },
            },
            required: ['day'],
          },
        },
      },
      required: ['days'],
    },
  },
  {
    name: 'invite_team_members',
    description:
      'Invite people to the team by email. Checks the account\'s user limit and role rules first, skips anyone already on ' +
      'the team or invited, and shows a confirm card. Roles: Super User (full access), Scheduling Assistant (books ' +
      'for others, takes no appointments), Team Member (takes appointments, sees their own).',
    input_schema: {
      type: 'object',
      properties: {
        people: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              firstName: { type: 'string' },
              lastName: { type: 'string' },
              email: { type: 'string' },
              role: { type: 'string', enum: SYSTEM_ROLE_NAMES },
            },
            required: ['firstName', 'email', 'role'],
          },
        },
      },
      required: ['people'],
    },
  },
  {
    name: 'update_team_member',
    description:
      'Change a team member\'s role, or resend or cancel a pending invite. Shows a confirm card.',
    input_schema: {
      type: 'object',
      properties: {
        person: { type: 'string', description: 'Team member or invitee name or email.' },
        action: { type: 'string', enum: ['change_role', 'resend_invite', 'cancel_invite'] },
        role: { type: 'string', enum: SYSTEM_ROLE_NAMES, description: 'change_role only.' },
      },
      required: ['person', 'action'],
    },
  },
  {
    name: 'present_choices',
    description:
      'Show tappable answers for a question with a few clear options (which services they offer, which form, ' +
      'yes/no on a setup step) instead of making them type. Their pick comes back as the next message.',
    input_schema: {
      type: 'object',
      properties: {
        prompt: { type: 'string', description: 'The question, short.' },
        options: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              label: { type: 'string' },
              description: { type: 'string', description: 'Optional one-line detail.' },
            },
            required: ['label'],
          },
          description: '2-8 options.',
        },
        allowMultiple: { type: 'boolean', description: 'Let them pick several, then tap Done.' },
        purpose: { type: 'string', description: 'What the answer is for, so you can continue when it comes back.' },
      },
      required: ['prompt', 'options'],
    },
  },
  {
    name: 'open_setup_page',
    description:
      // app-store-copy-ok: names the web billing page, which the app refuses to show
      'Show a button to the page for setup chat can\'t do: billing, logo upload (branding), calendar or ' +
      'text-messaging connections (integrations), drawing territories, the team page, deleting forms (booking_forms), ' +
      'or the full form editor (form_editor with bookingFormId).',
    input_schema: {
      type: 'object',
      properties: {
        page: { type: 'string', enum: SETUP_PAGES },
        reason: { type: 'string', description: 'Short line for the card, e.g. "Upload your logo".' },
        bookingFormId: { type: 'string', description: 'form_editor only.' },
      },
      required: ['page'],
    },
  },
];

export const SETUP_TOOL_NAMES: ReadonlySet<string> = new Set(SETUP_TOOLS.map(tool => tool.name));

// ─────────────────────────────────────────────────────────────────────────────
// Shared plumbing
// ─────────────────────────────────────────────────────────────────────────────

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string | null } | null } | null | undefined;
    return body?.error?.message || err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

function permissionDenied(action: string) {
  return {
    saved: false,
    permissionDenied: true,
    note:
      `The operator's role can't ${action}. Say so plainly in one sentence: a Super User on their team has to ` +
      'do it or give them access. Don\'t look for a workaround.',
  };
}

function rejected(errors: readonly string[], planLocked: readonly string[] = []) {
  return {
    saved: false,
    ...(errors.length > 0 ? { errors } : {}),
    ...(planLocked.length > 0 ? { planLocked } : {}),
    note: planLocked.length > 0
      ? 'Nothing was saved. Explain the planLocked items in a sentence or two, as your rules for planLocked say, ' +
        'and offer to go ahead without those parts.'
      : 'Nothing was saved. Ask about these in ONE short message (or fix them yourself when the conversation ' +
        'already answers them), then call again.',
  };
}

const declined = () => ({ saved: false, declined: true, note: 'They declined, so nothing changed.' });

/**
 * "Same-Day Bookings needs the Scale plan." when the plan doesn't include the feature, or
 * "Same-Day Bookings isn't available on this account." where plans can't be mentioned.
 */
function lockedReason(deps: RoutyToolDeps, feature: string): string | null {
  if (!deps.env.lockedFeatures?.includes(feature)) return null;
  const definition = getFeatureDefinition(feature);
  return deps.env.canMentionBilling
    ? `${definition.label} needs the ${tierLabel(definition.requiredTier)} plan.`
    : `${definition.label} isn't available on this account.`;
}

/** `STARTER_TEAM_MEMBER_ONLY_MESSAGE` without the plan name, for where plans can't be mentioned. */
const STARTER_ROLE_RULE = 'On this account, the second user can only be a Team Member.';

type Step = readonly [label: string, run: () => Promise<unknown>];

/** Run writes in order; a failure names what already saved, since nothing rolls back. */
async function runSteps(steps: readonly Step[]): Promise<void> {
  const saved: string[] = [];
  for (const [label, run] of steps) {
    try {
      await run();
    } catch (err) {
      const lead = saved.length > 0 ? `Saved ${saved.join(', ')}, but saving` : 'Saving';
      throw new Error(`${lead} ${label} failed: ${errorMessage(err)}`);
    }
    saved.push(label);
  }
}

/** False when nothing was shown: the billing page is never linked where billing can't be mentioned. */
function showSetupLink(deps: RoutyToolDeps, page: RoutySetupPage, reason?: string, bookingFormId?: string): boolean {
  if (page === 'billing' && !deps.env.canMentionBilling) return false;
  deps.onArtifact?.({ type: 'setup_link', page, ...(reason ? { reason } : {}), ...(bookingFormId ? { bookingFormId } : {}) });
  return true;
}

/** Keys the tool's schema doesn't define; ignoring them would report "nothing to change". */
function unknownKeys(input: Input, known: Iterable<string>): string[] {
  const allowed = new Set(known);
  return Object.keys(input).filter(key => !allowed.has(key));
}

const unknownKeysError = (keys: readonly string[]): string =>
  `Unknown ${keys.length === 1 ? 'field' : 'fields'}: ${keys.join(', ')}. Use only the fields this tool lists.`;

const textLines = (texts: readonly string[]): ConfirmLine[] => texts.map(text => ({ text }));

function personName(user: Routemize_DTOs_UserSmallDTO): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.userName || 'Someone';
}

function inviteName(invite: Routemize_DTOs_UserInviteDto): string {
  return [invite.firstName, invite.lastName].filter(Boolean).join(' ') || invite.email;
}

function formatAddress(address: Routemize_DTOs_CompanyLocationDto | Routemize_DTOs_UserAddressDto | null | undefined): string {
  if (!address) return 'None';
  const region = [address.state, address.zipCode].filter(Boolean).join(' ');
  return [address.addressLine1, address.city, region].filter(Boolean).join(', ') || 'None';
}

const hasCoordinates = (address: { latitude?: number | null; longitude?: number | null } | null | undefined): boolean =>
  !!address?.latitude && !!address?.longitude;

// ─────────────────────────────────────────────────────────────────────────────
// Loaders
// ─────────────────────────────────────────────────────────────────────────────

/** Fresh forms: setup may follow changes made elsewhere, so the session cache is skipped. */
async function loadForms(): Promise<Routemize_DTOs_BookingFormDto[]> {
  invalidateBookingFormCaches();
  return fetchRawBookingForms();
}

async function loadCompanyWeek(): Promise<Routemize_DTOs_CompanyWorkScheduleDTO[]> {
  return (await CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule()).dailySchedules ?? [];
}

async function loadTeamMembers(): Promise<TeamMember[]> {
  return (await UserService.getApiAppUserGetTeamMembers()) ?? [];
}

/** A form's settings, or null when it has none yet. Other failures throw. */
async function loadFormSettings(bookingFormId: string): Promise<Routemize_DTOs_BookingFormSettingDto | null> {
  try {
    return await BookingFormSettingService.getApiAppBookingFormSettingGetByFormId(bookingFormId);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || JSON.stringify(err.body ?? '').includes('EntityNotFoundException'))) {
      return null;
    }
    throw err;
  }
}

async function loadPendingInvites(): Promise<Routemize_DTOs_UserInviteDto[]> {
  const page = await UserService.getApiAppUserGetInviteList(undefined, 0, 100);
  return ((page.items ?? []) as Routemize_DTOs_UserInviteDto[]).filter(
    invite => invite.inviteStatus !== Routemize_Constants_InviteStatus.ACCEPTED && !invite.email.endsWith('@placeholder.local'),
  );
}

async function loadMasterData(key: 'EventTypes' | 'ReferralType'): Promise<Routemize_DTOs_MasterData[]> {
  const results = await MasterDataService.getApiAppMasterDataGet(key);
  return results?.[0]?.items ?? [];
}

/** The stored code for what someone called an event type or lead source. */
function matchMasterData(items: readonly Routemize_DTOs_MasterData[], value: unknown): string | null {
  const key = labelKey(cleanText(value));
  return items.find(item => labelKey(item.code) === key || labelKey(item.text) === key)?.code ?? null;
}

const masterDataOptions = (items: readonly Routemize_DTOs_MasterData[]): string =>
  items.map(item => item.text || item.code).filter(Boolean).join(', ');

type FormLookup =
  | { form: Routemize_DTOs_BookingFormDto; forms: Routemize_DTOs_BookingFormDto[] }
  | { result: Record<string, unknown> };

async function findForm(hint: unknown): Promise<FormLookup> {
  const value = cleanText(hint);
  if (!value) return { result: { found: false, note: 'Say which form: its name or id from list_booking_forms.' } };
  const forms = await loadForms();
  const matches = matchBookingForms(forms, value);
  if (matches.length === 1) return { form: matches[0], forms };
  if (matches.length === 0) {
    return {
      result: {
        found: false,
        note: `No form matches "${value}". Call list_booking_forms with includeDrafts=true and ask which one they mean.`,
      },
    };
  }
  return {
    result: {
      found: false,
      matches: matches.slice(0, 8).map(form => ({ id: form.id, name: form.formName, status: form.status })),
      note: 'Several forms match. Ask which one (present_choices works well), then call again with its id.',
    },
  };
}

function isDefaultLink(company: Routemize_DTOs_CompanySettingsDTO | null, form: Routemize_DTOs_BookingFormDto): boolean {
  const link = company?.defaultRoutemizeLink?.trim();
  return !!link && (link === form.slug || link === form.id);
}

/** Team members someone named, each active and able to take appointments. */
function resolveAssignees(members: readonly TeamMember[], raw: unknown, key: string, errors: string[]): TeamMember[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    errors.push(`${key} must be a list of names or emails.`);
    return [];
  }
  const picked: TeamMember[] = [];
  for (const value of raw) {
    const match = matchTeamMember(members, cleanText(value));
    if ('error' in match) errors.push(match.error);
    else if (match.member.isActive === false) errors.push(`${match.member.name} is deactivated.`);
    else if (!UserRoleConfigManager.isAssignableFromRoleNames(match.member.roleNames)) {
      errors.push(`${match.member.name} is a ${primaryRoleName(match.member.roleNames) ?? 'user'} and can't take appointments.`);
    } else if (!picked.includes(match.member)) picked.push(match.member);
  }
  return picked;
}

function usesPrequalification(edit: QuestionEdit): boolean {
  switch (edit.action) {
    case 'add':
      return !!edit.showWhen || (edit.requestOnlyAnswers?.length ?? 0) > 0;
    case 'update':
      return (edit.requestOnlyAnswers?.length ?? 0) > 0;
    case 'show_when':
      return true;
    default:
      return false;
  }
}

/** Template questions, flattened when the plan has no pre-qualification. */
function templateQuestions(
  deps: RoutyToolDeps,
  raw: unknown,
  bookingFormId: string | null,
  errors: string[],
  notes: string[],
): Question[] {
  const parsed = parseTemplateRequest(raw);
  errors.push(...parsed.errors);
  if (!parsed.request) return [];
  const built = buildTemplateQuestions(parsed.request, bookingFormId);
  errors.push(...built.errors);
  const locked = lockedReason(deps, 'FormPrequalification');
  if (built.questions.length === 0 || !locked) return built.questions;
  notes.push(`${locked} The template goes in without follow-up questions and request-only answers.`);
  return flattenTemplateQuestions(built.questions);
}

// ─────────────────────────────────────────────────────────────────────────────
// Reads
// ─────────────────────────────────────────────────────────────────────────────

type Settled<T> = { value: T } | { error: string };

async function settle<T>(promise: Promise<T>): Promise<Settled<T>> {
  try {
    return { value: await promise };
  } catch (err) {
    return { error: errorMessage(err) };
  }
}

async function execGetSetupOverview(deps: RoutyToolDeps) {
  deps.onActivity?.('Reading your setup');
  const admin = deps.env.canEditCompanySettings;
  const [settingsResult, weekResult, officesResult, formsResult, calendarResult, teamResult, invitesResult, usageResult] =
    await Promise.all([
      settle(CompanySettingsService.getApiAppCompanySettingsGet()),
      settle(loadCompanyWeek()),
      settle(CompanyLocationService.getApiAppCompanyLocationGetAllActive()),
      settle(loadForms()),
      settle(GoogleCalendarService.getApiAppGoogleCalendarGetCalendarIntegrationInfo()),
      admin ? settle(loadTeamMembers()) : null,
      admin ? settle(loadPendingInvites()) : null,
      admin && deps.env.canMentionBilling ? settle(SubscriptionService.getApiAppSubscriptionGetUsage()) : null,
    ]);
  const unavailable: Record<string, string> = {};
  function read<T>(section: string, result: Settled<T> | null): T | null {
    if (!result) return null;
    if ('error' in result) {
      unavailable[section] = result.error;
      return null;
    }
    return result.value;
  }
  const company = read('settings', settingsResult);
  const week = read('companyHours', weekResult);
  const offices = read('offices', officesResult);
  const forms = read('bookingForms', formsResult);
  const calendar = read('calendar', calendarResult);
  const members = read('team', teamResult);
  const invites = read('pendingInvites', invitesResult);
  const usage = read('seats', usageResult);
  const openForms = forms?.filter(form => form.status !== Routemize_Constants_BookingFormStatus.ARCHIVED) ?? null;
  const activeMembers = members?.filter(member => member.isActive !== false) ?? null;

  const lockedLabels = (deps.env.lockedFeatures ?? []).map(key => getFeatureDefinition(key).label);
  return {
    ...(deps.env.canMentionBilling
      ? { plan: { tier: deps.env.planTier ? tierLabel(deps.env.planTier) : 'Unknown', locked: lockedLabels } }
      : { notAvailableOnAccount: lockedLabels }),
    canChange: {
      companySettings: admin,
      bookingForms: deps.env.canEditBookingForms,
      createForms: deps.env.canCreateBookingForms,
    },
    ...(company
      ? {
        business: {
          name: company.businessName || 'Not set',
          website: company.businessWebsite || 'Not set',
          email: company.companyEmail || 'Not set',
          phone: company.phone || 'Not set',
          timeZone: company.timeZone || 'Not set',
          country: company.country || 'US',
        },
        settings: describeCompanySettings(company),
        serviceArea: company.serviceAreaMode || 'radius',
      }
      : {}),
    ...(week ? { companyHours: describeWeekSchedule(week) } : {}),
    ...(offices
      ? {
        offices: offices.map(office => ({
          name: office.locationName || 'Office',
          address: formatAddress(office),
          main: !!office.isDefault,
          onMap: hasCoordinates(office),
        })),
      }
      : {}),
    ...(openForms
      ? {
        bookingForms: openForms.slice(0, MAX_LISTED).map(form => ({
          id: form.id,
          name: form.formName || 'Untitled form',
          status: form.status ?? null,
          leadSource: form.leadSource || null,
          defaultLink: isDefaultLink(company, form),
        })),
        ...(openForms.length > MAX_LISTED ? { moreForms: openForms.length - MAX_LISTED } : {}),
      }
      : {}),
    ...(calendar
      ? {
        operatorCalendar: calendar.isConnectedToGoogle
          ? 'Google connected'
          : calendar.isConnectedToMicrosoft
            ? 'Microsoft connected'
            : 'Not connected',
      }
      : {}),
    textMessaging: deps.env.smsProviderConnected ? 'Connected' : 'Not connected',
    ...(activeMembers
      ? {
        team: activeMembers.slice(0, MAX_LISTED).map(member => ({
          name: member.name || member.email,
          email: member.email,
          role: primaryRoleName(member.roleNames) ?? (member.roleNames ?? []).join(', '),
          homeAddress: member.homeAddress ? 'Saved' : 'Missing',
        })),
      }
      : {}),
    ...(invites
      ? {
        pendingInvites: invites.slice(0, MAX_LISTED).map(invite => ({
          name: inviteName(invite),
          email: invite.email,
          role: invite.roleName || null,
        })),
      }
      : {}),
    ...(usage ? { seats: { used: usage.activeUsers ?? null, included: usage.maxUsers ?? null } } : {}),
    ...(Object.keys(unavailable).length > 0 ? { unavailable } : {}),
    note:
      'Answer what they asked from this. For "what is left to set up" lead with the gaps that block bookings ' +
      '(no published form, missing hours, offices or home addresses not on the map, nobody assigned) before ' +
      'nice-to-haves. Sections under unavailable failed to load; say so instead of guessing.',
  };
}

async function execGetBookingForm(input: Input, deps: RoutyToolDeps) {
  const found = await findForm(input.form);
  if ('result' in found) return found.result;
  const formId = found.form.id!;
  deps.onActivity?.(`Reading ${found.form.formName || 'the form'}`);
  const [form, settings, questions, company] = await Promise.all([
    BookingFormService.getApiAppBookingFormGetById(formId),
    loadFormSettings(formId),
    BookingFormQuestionService.getApiAppBookingFormQuestionGetFormQuestion(formId),
    CompanySettingsService.getApiAppCompanySettingsGet(),
  ]);
  return {
    id: formId,
    name: form.formName || 'Untitled form',
    status: form.status ?? null,
    publicLink: form.publicLink || null,
    eventType: form.eventType || null,
    leadSource: form.leadSource || 'Not tracked',
    defaultLink: isDefaultLink(company, form),
    assignees: (form.assignedUsers ?? []).map(personName),
    settings: describeFormSettings(form, settings),
    questions: summarizeQuestions(questions ?? []),
    note:
      'Use this to answer questions about the form or to plan a change. Change settings with update_booking_form ' +
      'and questions with edit_form_questions; never book anything from here.',
  };
}

function execListFormTemplates(input: Input) {
  const industry = cleanText(input.industry);
  return {
    ...describeFormTemplates(industry || undefined),
    note: industry
      ? 'Offer the services with present_choices (allowMultiple) unless they already said which they do.'
      : 'Pick the closest industry. If none fits, build the questions with edit_form_questions instead.',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Booking forms
// ─────────────────────────────────────────────────────────────────────────────

async function execCreateBookingForm(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canCreateBookingForms) return permissionDenied('create booking forms');
  deps.onActivity?.('Checking your plan');
  if (!(await SubscriptionService.postApiAppSubscriptionCheckLimit('bookingforms', 1))) {
    const linked = showSetupLink(deps, 'billing', 'Upgrade for more booking forms'); // app-store-copy-ok: web only, the app never shows the billing card
    return {
      saved: false,
      limitReached: true,
      note: linked
        ? 'The plan\'s booking form limit is reached (Starter 2, Pro 5, Scale unlimited). Say so in one sentence; ' +
          'the card opens the plans page. Editing an existing form is the other option.'
        : 'This account is at its booking form limit. Say so in one sentence and offer to edit an existing form instead.',
    };
  }

  deps.onActivity?.('Getting the form ready');
  const wantsLeadSource = cleanText(input.leadSource) !== '';
  const [defaults, week, members, eventTypes, leadSources, forms] = await Promise.all([
    BookingFormSettingService.getApiAppBookingFormSettingGetDefaults(),
    loadCompanyWeek(),
    loadTeamMembers(),
    loadMasterData('EventTypes'),
    wantsLeadSource ? loadMasterData('ReferralType') : Promise.resolve([]),
    loadForms(),
  ]);
  const { form, errors } = resolveNewBookingForm(defaults, week, input);
  const notes = [...form.notes];
  const locked: string[] = [];
  const roundRobin = form.detail.eventNature === Routemize_Constants_EventNature.ROUND_ROBIN;
  if (input.sameDayBooking === true) {
    const reason = lockedReason(deps, 'SameDayBookings');
    if (reason) locked.push(reason);
  }
  if (roundRobin) {
    const reason = lockedReason(deps, 'SmartAssignment');
    if (reason) locked.push(reason);
  }

  let eventType = matchMasterData(eventTypes, DEFAULT_EVENT_TYPE) ?? eventTypes[0]?.code ?? null;
  if (input.eventType !== undefined) {
    eventType = matchMasterData(eventTypes, input.eventType);
    if (!eventType) errors.push(`eventType must be one of: ${masterDataOptions(eventTypes)}.`);
  }
  let leadSource: string | null = null;
  if (wantsLeadSource) {
    const reason = lockedReason(deps, 'FormPrequalification');
    if (reason) locked.push(`Lead source tracking: ${reason}`);
    else {
      leadSource = matchMasterData(leadSources, input.leadSource);
      if (!leadSource) errors.push(`leadSource must be one of: ${masterDataOptions(leadSources)}.`);
    }
  }

  let assignees = resolveAssignees(members, input.assignees, 'assignees', errors);
  if (input.assignees === undefined) {
    const operator = members.find(member => member.id === deps.env.currentUserId);
    if (operator && UserRoleConfigManager.isAssignableFromRoleNames(operator.roleNames)) assignees = [operator];
    else errors.push('Say who takes these appointments (assignees); the operator\'s role can\'t take them.');
  } else if (assignees.length === 0 && errors.length === 0) {
    errors.push('Pick at least one person to take these appointments.');
  }
  if (roundRobin && assignees.length < 2) errors.push('Round robin needs at least two people assigned.');

  const questions = input.template === undefined
    ? []
    : orderQuestionsForBuilder(templateQuestions(deps, input.template, null, errors, notes));
  if (locked.length > 0 || errors.length > 0) return rejected(errors, locked);

  const name = form.detail.formName ?? '';
  if (forms.some(existing => labelKey(existing.formName) === labelKey(name) && existing.status !== Routemize_Constants_BookingFormStatus.ARCHIVED)) {
    notes.push(`There's already a form named "${name}".`);
  }
  const publish = input.publish === true;
  const { settings } = form;
  const starts = `${formatTimeString(settings.firstAppointmentTime ?? undefined)} – ${formatTimeString(settings.lastAppointmentTime ?? undefined)}`;
  const approved = await deps.requestConfirmation({
    title: publish ? `Create and publish "${name}"?` : `Create "${name}" as a draft?`,
    subtitle: settings.serviceTypeLabel ?? undefined,
    lines: [
      { icon: 'clock', text: `${formatDurationMinutes(settings.timeAtLocationMinutes ?? 45)} visits, starting ${starts}` },
      { icon: 'calendar', text: formatDayList(formBookableDays(settings)) },
      {
        icon: 'user',
        text: `${assignees.map(member => member.name || member.email).join(', ')}${roundRobin ? ' (round robin)' : ''}`,
      },
      {
        text: `${form.detail.allowInstantBooking === false ? 'Bookings arrive as requests' : 'Customers book instantly'}` +
          `${leadSource ? `, lead source ${leadSource}` : ''}`,
      },
      ...textLines(notes),
    ],
    outline: questions.length > 0 ? buildQuestionOutline([], questions) : undefined,
    confirmLabel: publish ? 'Create and publish' : 'Create draft',
  });
  if (!approved) return declined();

  deps.onActivity?.('Creating the form');
  let created: Routemize_DTOs_BookingFormDto;
  try {
    created = await BookingFormService.postApiAppBookingFormCreateForm({
      formDetail: { ...form.detail, eventType, leadSource },
      questions: questions.map((question, index) => ({
        label: question.label,
        type: question.type,
        isRequired: question.isRequired,
        order: index + 1,
        optionsJson: question.optionsJson,
      })),
      formSettings: form.settings,
      assignedUserIds: assignees.map(member => member.id!),
    });
  } finally {
    invalidateBookingFormCaches();
  }
  notifySetupChanged('booking_forms');

  let publishError: string | null = null;
  if (publish && created.id) {
    try {
      await BookingFormService.postApiAppBookingFormPublishBookingForm(created.id);
    } catch (err) {
      publishError = errorMessage(err);
    }
    invalidateBookingFormCaches();
    notifySetupChanged('booking_forms');
  }
  return {
    saved: true,
    id: created.id,
    name,
    status: publish && !publishError ? 'Published' : 'Draft',
    questions: questions.length,
    ...(publishError ? { publishError } : {}),
    note: publishError
      ? 'The form was created as a draft but publishing failed; tell them why and offer to try again.'
      : publish
        ? 'Confirm in one sentence. It can take bookings now.'
        : 'Confirm in one sentence and offer to publish it when they\'re ready.',
  };
}

async function execUpdateBookingForm(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditBookingForms) return permissionDenied('edit booking forms');
  const found = await findForm(input.form);
  if ('result' in found) return found.result;
  const formId = found.form.id!;
  deps.onActivity?.(`Loading ${found.form.formName || 'the form'}`);
  const needsCompany = input.makeDefaultLink !== undefined || input.status === 'archive';
  const [form, stored, week, company] = await Promise.all([
    BookingFormService.getApiAppBookingFormGetById(formId),
    loadFormSettings(formId),
    loadCompanyWeek(),
    needsCompany ? CompanySettingsService.getApiAppCompanySettingsGet() : Promise.resolve(null),
  ]);
  const name = form.formName || 'Untitled form';
  const resolved = resolveBookingFormChanges(form, stored, week, input);
  const { detail, settings } = resolved.changes;
  const errors = [...resolved.errors];
  const rows = [...resolved.rows];
  const notes = [...resolved.changes.notes];
  const locked: string[] = [];

  if (detail.allowSameDayBooking === true && form.allowSameDayBooking === false) {
    const reason = lockedReason(deps, 'SameDayBookings');
    if (reason) locked.push(reason);
  }
  const natureChanged = detail.eventNature !== undefined && detail.eventNature !== form.eventNature;
  const roundRobin = (detail.eventNature ?? form.eventNature) === Routemize_Constants_EventNature.ROUND_ROBIN;
  if (natureChanged && roundRobin) {
    const reason = lockedReason(deps, 'SmartAssignment');
    if (reason) locked.push(reason);
  }

  const assigned = (form.assignedUsers ?? []).filter(user => !!user.id);
  const currentIds = assigned.map(user => user.id!);
  let nextIds = currentIds;
  if (input.addAssignees !== undefined || input.removeAssignees !== undefined) {
    const members = await loadTeamMembers();
    const add = resolveAssignees(members, input.addAssignees, 'addAssignees', errors);
    const remove: string[] = [];
    if (input.removeAssignees !== undefined) {
      if (!Array.isArray(input.removeAssignees)) errors.push('removeAssignees must be a list of names or emails.');
      else {
        const assignedPeople = assigned.map(user => ({ id: user.id, name: personName(user), email: user.email }));
        for (const value of input.removeAssignees) {
          const match = matchTeamMember(assignedPeople, cleanText(value));
          if ('member' in match) {
            remove.push(match.member.id!);
            continue;
          }
          const onTeam = matchTeamMember(members, cleanText(value));
          errors.push('member' in onTeam ? `${onTeam.member.name} isn't assigned to this form.` : match.error);
        }
      }
    }
    nextIds = nextAssigneeIds(currentIds, add.map(member => member.id!), remove);
    if (nextIds.length === 0) errors.push('A form needs at least one person assigned.');
    const nameOf = (id: string) => {
      const user = assigned.find(u => u.id === id);
      return user ? personName(user) : members.find(m => m.id === id)?.name ?? 'Someone';
    };
    const before = currentIds.map(nameOf).join(', ') || 'Nobody';
    const after = nextIds.map(nameOf).join(', ') || 'Nobody';
    if (before !== after) rows.push({ label: 'Assigned', from: before, to: after });
  }
  const assigneesChanged = nextIds.join() !== currentIds.join();
  if ((natureChanged || assigneesChanged) && roundRobin && nextIds.length < 2) {
    errors.push('Round robin needs at least two people assigned.');
  }

  const status = input.status;
  const statusSteps: Step[] = [];
  if (status !== undefined) {
    if (status === 'publish') {
      if (form.status === Routemize_Constants_BookingFormStatus.PUBLISHED) notes.push('It\'s already published.');
      else if (form.status !== Routemize_Constants_BookingFormStatus.DRAFT) errors.push('Only drafts can be published; this form is archived.');
      else {
        rows.push({ label: 'Status', from: 'Draft', to: 'Published' });
        statusSteps.push(['publishing', () => BookingFormService.postApiAppBookingFormPublishBookingForm(formId)]);
      }
    } else if (status === 'unpublish') {
      if (form.status !== Routemize_Constants_BookingFormStatus.PUBLISHED) errors.push('Only published forms can be unpublished.');
      else {
        rows.push({ label: 'Status', from: 'Published', to: 'Unpublished (draft)' });
        statusSteps.push(['unpublishing', () => BookingFormService.postApiAppBookingFormUnpublishBookingForm(formId)]);
      }
    } else if (status === 'archive') {
      if (form.status === Routemize_Constants_BookingFormStatus.ARCHIVED) errors.push('It\'s already archived.');
      else {
        rows.push({ label: 'Status', from: form.status ?? 'Draft', to: 'Archived' });
        statusSteps.push(['archiving', () => BookingFormService.postApiAppBookingFormArchiveBookingForm(formId)]);
        if (isDefaultLink(company, form)) notes.push('This is the default booking link, so pick another form for it.');
      }
    } else {
      errors.push('status must be publish, unpublish or archive.');
    }
  }

  let defaultLinkStep: Step | null = null;
  if (input.makeDefaultLink !== undefined) {
    if (input.makeDefaultLink !== true) errors.push('makeDefaultLink only takes true; make another form the default instead.');
    else if (!deps.env.canEditCompanySettings) errors.push('Changing the default booking link needs company settings access.');
    else if (status === 'archive') errors.push('An archived form can\'t be the default booking link.');
    else if (company && !isDefaultLink(company, form)) {
      const currentLink = company.defaultRoutemizeLink?.trim();
      const current = found.forms.find(f => !!currentLink && (f.slug === currentLink || f.id === currentLink));
      rows.push({ label: 'Default booking link', from: current?.formName || (currentLink ? 'Another form' : 'Not set'), to: name });
      const identifier = form.slug || formId;
      defaultLinkStep = ['the default booking link', () =>
        CompanySettingsService.putApiAppCompanySettingsUpdate(
          company.id!,
          buildCompanySettingsPatch(company, { defaultRoutemizeLink: identifier }),
        )];
      if (form.status !== Routemize_Constants_BookingFormStatus.PUBLISHED && status !== 'publish') {
        notes.push('It isn\'t published, so the link won\'t take bookings until it is.');
      }
    } else if (company) notes.push('It\'s already the default booking link.');
  }

  if (locked.length > 0 || errors.length > 0) return rejected(errors, locked);
  const detailChanged = Object.keys(detail).length > 0 || assigneesChanged;
  const settingsChanged = Object.keys(settings).length > 0;
  if (rows.length === 0 && !detailChanged && !settingsChanged && statusSteps.length === 0 && !defaultLinkStep) {
    return { saved: false, note: notes.join(' ') || 'Those already match, so there\'s nothing to change.' };
  }

  const archiving = status === 'archive';
  const approved = await deps.requestConfirmation({
    title: archiving ? `Archive "${name}"?` : `Update "${name}"?`,
    lines: textLines(notes),
    changes: rows,
    confirmLabel: archiving ? 'Archive form' : 'Save changes',
    tone: archiving ? 'danger' : 'default',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving the form');
  const steps: Step[] = [];
  if (detailChanged) {
    steps.push(['the form details', () =>
      BookingFormService.putApiAppBookingFormUpdateForm({
        formDetail: buildFormDetailUpdate(form, detail),
        assignedUserIds: nextIds,
      })]);
  }
  if (settingsChanged) {
    steps.push(['the booking settings', async () => {
      if (stored) {
        await BookingFormSettingService.putApiAppBookingFormSettingUpdate(buildFormSettingsUpdate(formId, stored, settings));
        return;
      }
      const defaults = await BookingFormSettingService.getApiAppBookingFormSettingGetDefaults();
      await BookingFormSettingService.postApiAppBookingFormSettingCreate({
        ...buildNewFormSettings(defaults, { serviceLabel: settings.serviceTypeLabel ?? name }),
        ...settings,
        bookingFormId: formId,
      });
    }]);
  }
  steps.push(...statusSteps);
  if (defaultLinkStep) steps.push(defaultLinkStep);
  try {
    await runSteps(steps);
  } finally {
    invalidateBookingFormCaches();
    notifySetupChanged('booking_forms');
    if (defaultLinkStep) notifySetupChanged('company_settings');
  }
  return {
    saved: true,
    form: name,
    changed: rows.map(row => row.label),
    note: 'Confirm in one short sentence; the card already listed each change.',
  };
}

async function execEditFormQuestions(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditBookingForms) return permissionDenied('edit booking forms');
  if (input.edits === undefined && input.template === undefined) return rejected(['Give edits, a template, or both.']);
  const parsed = input.edits === undefined ? { edits: [], errors: [] } : parseQuestionEdits(input.edits);
  if (parsed.errors.length > 0) return rejected(parsed.errors);
  const errors: string[] = [];
  const notes: string[] = [];
  const prequalification = lockedReason(deps, 'FormPrequalification');
  if (prequalification && parsed.edits.some(usesPrequalification)) {
    return rejected([], [`Follow-up questions and request-only answers: ${prequalification}`]);
  }

  const found = await findForm(input.form);
  if ('result' in found) return found.result;
  const formId = found.form.id!;
  const name = found.form.formName || 'Untitled form';
  deps.onActivity?.(`Loading the questions on ${name}`);
  const original = (await BookingFormQuestionService.getApiAppBookingFormQuestionGetFormQuestion(formId)) ?? [];
  const insert = input.template === undefined ? undefined : templateQuestions(deps, input.template, formId, errors, notes);
  if (errors.length > 0) return rejected(errors);

  const replaceExisting = input.replaceExisting === true;
  const result = applyQuestionEdits(original, parsed.edits, { insert, replaceExisting });
  if (result.errors.length > 0) return rejected(result.errors);
  const writes = diffQuestions(original, result.questions, formId);
  const { creates, updates, deleteIds } = writes;
  if (creates.length === 0 && updates.length === 0 && deleteIds.length === 0) {
    return { saved: false, note: 'The questions already look like that, so nothing would change.' };
  }

  const counts = [
    creates.length > 0 ? `${creates.length} new` : '',
    updates.length > 0 ? `${updates.length} changed` : '',
    deleteIds.length > 0 ? `${deleteIds.length} removed` : '',
  ].filter(Boolean).join(', ');
  const approved = await deps.requestConfirmation({
    title: `Update the questions on "${name}"?`,
    subtitle: counts,
    lines: textLines([...result.warnings, ...notes]),
    outline: buildQuestionOutline(original, result.questions),
    confirmLabel: 'Save questions',
    tone: deleteIds.length > 0 ? 'danger' : 'default',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving the questions');
  const labelOf = (id: string | undefined) => original.find(q => q.id === id)?.label || 'a question';
  const failures: string[] = [];
  for (const id of deleteIds) {
    try {
      await BookingFormQuestionService.deleteApiAppBookingFormQuestionDelete(id);
    } catch (err) {
      failures.push(`Removing "${labelOf(id)}": ${errorMessage(err)}`);
    }
  }
  for (const update of updates) {
    try {
      await BookingFormQuestionService.putApiAppBookingFormQuestionUpdate(update);
    } catch (err) {
      failures.push(`Changing "${labelOf(update.id)}": ${errorMessage(err)}`);
    }
  }
  if (creates.length > 0) {
    try {
      await BookingFormQuestionService.postApiAppBookingFormQuestionCreateMany(creates);
    } catch (err) {
      failures.push(`Adding ${creates.length} new question${creates.length === 1 ? '' : 's'}: ${errorMessage(err)}`);
    }
  }
  invalidateBookingFormCaches();
  notifySetupChanged('booking_forms');
  return {
    saved: failures.length === 0,
    form: name,
    added: creates.length,
    changed: updates.length,
    removed: deleteIds.length,
    ...(failures.length > 0 ? { failures } : {}),
    ...(result.warnings.length > 0 ? { warnings: result.warnings } : {}),
    note: failures.length > 0
      ? 'Some writes failed (failures). Tell them exactly which, and offer to retry or open the form editor ' +
        '(open_setup_page form_editor).'
      : 'Confirm in one short sentence; the card already showed the questions.',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Company
// ─────────────────────────────────────────────────────────────────────────────

async function execUpdateBusinessProfile(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditCompanySettings) return permissionDenied('change the business profile');
  deps.onActivity?.('Loading your business profile');
  const company = await CompanySettingsService.getApiAppCompanySettingsGet();
  const resolved = resolveBusinessProfile(company, input);
  if (resolved.errors.length > 0) return rejected(resolved.errors);
  const { branding, settings } = resolved.changes;
  if (resolved.rows.length === 0) return { saved: false, note: 'Those already match, so there\'s nothing to change.' };

  const approved = await deps.requestConfirmation({
    title: 'Update your business profile?',
    lines: [],
    changes: resolved.rows,
    confirmLabel: 'Save changes',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving your business profile');
  const steps: Step[] = [];
  if (Object.keys(branding).length > 0) {
    steps.push(['the branding', () => BusinessBrandingService.putApiAppBusinessBrandingUpdate(branding)]);
  }
  if (Object.keys(settings).length > 0) {
    steps.push(['the company settings', () =>
      CompanySettingsService.putApiAppCompanySettingsUpdate(company.id!, buildCompanySettingsPatch(company, settings))]);
  }
  try {
    await runSteps(steps);
  } finally {
    notifySetupChanged('company_settings');
  }
  return { saved: true, changed: resolved.rows.map(row => row.label), note: 'Confirm in one short sentence.' };
}

async function execUpdateCompanyHours(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditCompanySettings) return permissionDenied('change company hours');
  if (!Array.isArray(input.days) || input.days.length === 0) return rejected(['days must list at least one day.']);
  const errors: string[] = [];
  const changes: DayHoursChange[] = [];
  input.days.forEach((raw, index) => {
    const entry = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const day = parseWeekday(entry.day);
    if (!day) {
      errors.push(`days[${index}]: day must be a weekday like "Saturday".`);
      return;
    }
    const time = (key: string): string | undefined => {
      if (entry[key] === undefined) return undefined;
      if (typeof entry[key] === 'string') return entry[key] as string;
      errors.push(`${day}: ${key} must be a time like "08:00".`);
      return undefined;
    };
    const lunch = (key: string): string | null | undefined => (entry[key] === null ? null : time(key));
    if (entry.open !== undefined && typeof entry.open !== 'boolean') errors.push(`${day}: open must be true or false.`);
    changes.push({
      day,
      open: typeof entry.open === 'boolean' ? entry.open : undefined,
      start: time('start'),
      end: time('end'),
      lunchStart: lunch('lunchStart'),
      lunchEnd: lunch('lunchEnd'),
    });
  });
  if (errors.length > 0) return rejected(errors);

  deps.onActivity?.('Loading your company hours');
  const merged = mergeWeekSchedule(await loadCompanyWeek(), changes);
  if (merged.errors.length > 0) return rejected(merged.errors);
  if (merged.rows.length === 0) return { saved: false, note: 'The hours already look like that, so nothing would change.' };

  const approved = await deps.requestConfirmation({
    title: 'Update your company hours?',
    lines: [{ icon: 'clock', text: 'Booking forms and availability follow these hours.' }],
    changes: merged.rows,
    confirmLabel: 'Save hours',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving your company hours');
  try {
    await CompanyWorkScheduleService.putApiAppCompanyWorkScheduleUpdateEnhancedWorkSchedule({ dailySchedules: merged.schedule });
  } finally {
    notifySetupChanged('company_hours');
  }
  return {
    saved: true,
    changed: merged.rows.map(row => row.label),
    note:
      'Confirm in one short sentence. If they shortened a day, offer to check booking forms whose window now ' +
      'falls outside the hours (get_booking_form).',
  };
}

async function execUpdateCompanySettings(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditCompanySettings) return permissionDenied('change company settings');
  deps.onActivity?.('Loading your settings');
  const company = await CompanySettingsService.getApiAppCompanySettingsGet();
  const resolved = resolveCompanySettings(company, input);
  const errors = [...resolved.errors];
  const routes = resolved.changes.routePreferences;
  if (routes?.defaultStartingLocation === 'office' || routes?.defaultEndingLocation === 'office') {
    const offices = await CompanyLocationService.getApiAppCompanyLocationGetAllActive();
    if (offices.length === 0) errors.push('There\'s no office address yet. Save one first (save_address, kind office).');
  }
  if (errors.length > 0) return rejected(errors);
  if (resolved.rows.length === 0) return { saved: false, note: 'Those already match, so there\'s nothing to change.' };

  const approved = await deps.requestConfirmation({
    title: 'Update your company settings?',
    lines: [],
    changes: resolved.rows,
    confirmLabel: 'Save settings',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving your settings');
  try {
    await CompanySettingsService.putApiAppCompanySettingsUpdate(company.id!, buildCompanySettingsPatch(company, resolved.changes));
  } finally {
    notifySetupChanged('company_settings');
  }
  return { saved: true, changed: resolved.rows.map(row => row.label), note: 'Confirm in one short sentence.' };
}

async function execSaveAddress(input: Input, deps: RoutyToolDeps) {
  const kind = input.kind;
  if (kind !== 'office' && kind !== 'home') return rejected(['kind must be office or home.']);
  if (kind === 'office' && !deps.env.canEditCompanySettings) return permissionDenied('change company locations');
  const address = cleanText(input.address);
  if (!address) return rejected(['Give the full street address with city, state and ZIP.']);

  deps.onActivity?.('Looking up the address');
  const geo = await geocodeOrThrow(address);
  const parts = parseFormattedAddress(geo.formatted);
  if (!parts.city || !parts.state || !parts.zipCode) {
    return rejected([`"${geo.formatted}" isn't a full street address. Ask for the street, city, state and ZIP.`]);
  }
  const unit = cleanText(input.unit) || null;
  return kind === 'office'
    ? saveOfficeAddress(input, deps, geo.formatted, { ...parts, addressLine2: unit })
    : saveHomeAddress(deps, geo.formatted, { ...parts, addressLine2: unit });
}

type ParsedAddress = ReturnType<typeof parseFormattedAddress> & { addressLine2: string | null };

async function saveOfficeAddress(input: Input, deps: RoutyToolDeps, formatted: string, parts: ParsedAddress) {
  const offices = await CompanyLocationService.getApiAppCompanyLocationGetAllActive();
  const main = offices.find(office => office.isDefault) ?? offices[0] ?? null;
  const addAsNew = input.addAsNew === true && main !== null;
  const givenName = cleanText(input.locationName);
  if (addAsNew && !givenName) return rejected(['Name the new location (locationName), e.g. "North Shop".']);
  const name = givenName || main?.locationName || 'Main Office';
  const makeDefault = addAsNew && input.makeDefault === true;

  const approved = await deps.requestConfirmation({
    title: addAsNew ? `Add ${name}?` : main ? `Move ${name}?` : 'Add your office?',
    lines: [
      { icon: 'map', text: formatted },
      ...(makeDefault ? [{ text: 'It becomes the main office routes start and end at.' }] : []),
    ],
    changes: addAsNew ? undefined : [{ label: name, from: formatAddress(main), to: formatted }],
    confirmLabel: addAsNew ? 'Add location' : 'Save address',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving the office');
  const body: Routemize_DTOs_CreateUpdateCompanyLocationDto = {
    locationName: name,
    addressLine1: parts.addressLine1,
    addressLine2: parts.addressLine2,
    city: parts.city,
    state: parts.state,
    zipCode: parts.zipCode,
    country: parts.country,
    locationType: addAsNew
      ? Routemize_Constants_BaseLocation.BRANCH_OFFICE
      : main?.locationType ?? Routemize_Constants_BaseLocation.MAIN_OFFICE,
    isActive: true,
    isDefault: !addAsNew,
  };
  let savedId: string | undefined;
  try {
    const saved = main && !addAsNew
      ? await CompanyLocationService.putApiAppCompanyLocationUpdate(main.id!, body)
      : await CompanyLocationService.postApiAppCompanyLocationCreate(body);
    savedId = saved.id;
    if (makeDefault && savedId) await CompanyLocationService.postApiAppCompanyLocationSetDefault(savedId);
  } finally {
    notifySetupChanged('locations');
  }
  const after = (await CompanyLocationService.getApiAppCompanyLocationGetAllActive()).find(office => office.id === savedId);
  const onMap = hasCoordinates(after);
  return {
    saved: true,
    office: name,
    address: formatted,
    onMap,
    note: onMap
      ? 'Confirm in one short sentence.'
      : 'It saved, but the map lookup didn\'t place it, so routes can\'t start there yet. Ask them to check the address.',
  };
}

async function saveHomeAddress(deps: RoutyToolDeps, formatted: string, parts: ParsedAddress) {
  const userId = deps.env.currentUserId;
  const addresses = (await UserService.getApiAppUserGetUserAddresses(userId)) ?? [];
  const entry = homeAddressUpsert(addresses, parts);
  const current = addresses.find(address => !!entry.id && address.id === entry.id) ?? null;

  const approved = await deps.requestConfirmation({
    title: current ? 'Update your home address?' : 'Add your home address?',
    lines: [{ icon: 'map', text: 'Routes that start or end at home use this.' }],
    changes: [{ label: 'Home', from: formatAddress(current), to: formatted }],
    confirmLabel: 'Save address',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving your address');
  try {
    await UserService.postApiAppUserCreateUserAddress({ userId, addresses: [entry] });
  } finally {
    notifySetupChanged('team');
  }
  const after = ((await UserService.getApiAppUserGetUserAddresses(userId)) ?? []).find(
    address => address.addressType === Routemize_Constants_AddressType.HOME && address.isActive !== false,
  );
  const onMap = hasCoordinates(after);
  return {
    saved: true,
    address: formatted,
    onMap,
    note: onMap
      ? 'Confirm in one short sentence.'
      : 'It saved, but the map lookup didn\'t place it, so routes can\'t start there yet. Ask them to check the address.',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// People
// ─────────────────────────────────────────────────────────────────────────────

async function execUpdateWeeklyAvailability(input: Input, deps: RoutyToolDeps) {
  if (!Array.isArray(input.days) || input.days.length === 0) return rejected(['days must list at least one day.']);
  const errors: string[] = [];
  const changes: WeeklyHoursChange[] = [];
  input.days.forEach((raw, index) => {
    const entry = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const day = parseWeekday(entry.day);
    if (!day) {
      errors.push(`days[${index}]: day must be a weekday like "Friday".`);
      return;
    }
    if (entry.off === true || entry.open === false) {
      errors.push(
        `${day}: a weekly day off on someone's own schedule isn't something Routy can set. For specific dates use ` +
        'block_days_off; for every week, the work schedule screen.',
      );
      return;
    }
    const text = (key: string) => (typeof entry[key] === 'string' ? (entry[key] as string) : undefined);
    changes.push({ day, start: text('start'), end: text('end'), useCompanyHours: entry.useCompanyHours === true });
  });
  if (errors.length > 0) return rejected(errors);

  let userId = deps.env.currentUserId;
  let whose = 'your';
  const person = cleanText(input.person);
  if (person) {
    const members = await loadTeamMembers();
    const match = matchTeamMember(members, person);
    if ('error' in match) return rejected([match.error]);
    if (match.member.id !== deps.env.currentUserId) {
      if (!deps.env.canManageAllBookings) return permissionDenied('change other people\'s working hours');
      userId = match.member.id!;
      whose = `${match.member.name || match.member.email}'s`;
    }
  }

  deps.onActivity?.('Loading the work schedule');
  const schedule = await userWorkScheduleManager.getEffectiveSchedule(userId);
  const plan = planWeeklyAvailability(schedule, changes);
  if (plan.errors.length > 0) return rejected(plan.errors);
  if (plan.steps.length === 0) return { saved: false, note: 'Those hours already apply, so nothing would change.' };

  const approved = await deps.requestConfirmation({
    title: `Update ${whose} weekly hours?`,
    lines: [{ icon: 'repeat', text: 'Every week from now on.' }],
    changes: plan.rows,
    confirmLabel: 'Save hours',
  });
  if (!approved) return declined();

  deps.onActivity?.('Saving the work schedule');
  try {
    await runSteps(plan.steps.map(step => [
      step.day.dayLabel,
      () => step.kind === 'revert'
        ? userWorkScheduleManager.disableDayOverride(userId, step.day.userOverrideIds)
        : userWorkScheduleManager.updateDayHours(userId, step.day.dayOfWeek, step.start!, step.end!, step.day.userOverrideIds),
    ] as const));
  } finally {
    notifySetupChanged('availability');
  }
  return { saved: true, changed: plan.rows.map(row => row.label), note: 'Confirm in one short sentence.' };
}

async function execInviteTeamMembers(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditCompanySettings) return permissionDenied('invite team members');
  const parsed = parseInvites(input.people);
  if (parsed.errors.length > 0) return rejected(parsed.errors);

  deps.onActivity?.('Checking your team');
  const [members, pending, usage] = await Promise.all([
    loadTeamMembers(),
    loadPendingInvites(),
    SubscriptionService.getApiAppSubscriptionGetUsage(),
  ]);
  const skipped: string[] = [];
  const fresh = parsed.invites.filter(invite => {
    const member = members.find(m => labelKey(m.email) === invite.email);
    if (member) skipped.push(`${invite.email} is already on the team${member.isActive === false ? ' (deactivated)' : ''}.`);
    else if (pending.some(p => labelKey(p.email) === invite.email)) {
      skipped.push(`${invite.email} already has a pending invite (resend it with update_team_member).`);
    } else return true;
    return false;
  });
  if (fresh.length === 0) return { saved: false, skipped, note: 'Nobody new to invite; tell them why.' };

  const allowed = starterRoleAllowance({
    tier: deps.env.planTier ?? null,
    additionalSeats: usage.additionalSeats ?? 0,
    members,
    invites: fresh,
  });
  const blocked = fresh.filter((_, index) => !allowed[index]);
  if (blocked.length > 0) {
    const emails = blocked.map(invite => invite.email).join(', ');
    return rejected([], [deps.env.canMentionBilling // app-store-copy-ok: web only (canMentionBilling)
      ? `${emails}: ${STARTER_TEAM_MEMBER_ONLY_MESSAGE} Invite them as Team Members, add a seat, or upgrade to Pro.`
      : `${emails}: ${STARTER_ROLE_RULE} Invite them as Team Members instead.`]);
  }
  if (!(await SubscriptionService.postApiAppSubscriptionCheckLimit('users', 1))) {
    // app-store-copy-ok: web only (canMentionBilling)
    const linked = deps.env.canMentionBilling && showSetupLink(deps, 'team', 'Add a seat to invite more people');
    return {
      saved: false,
      seatsFull: true,
      note: linked // app-store-copy-ok: web only (canMentionBilling)
        ? 'Every seat is in use. Say so in one sentence; the card opens the team page, where they can add a seat ' +
          'and send the invite.'
        : 'This account can\'t add more team members. Say so in one sentence.',
    };
  }

  const approved = await deps.requestConfirmation({
    title: fresh.length === 1 ? `Invite ${fresh[0].firstName}?` : `Invite ${fresh.length} people?`,
    subtitle: deps.env.canMentionBilling && usage.maxUsers
      ? `${usage.activeUsers ?? 0} of ${usage.maxUsers} seats in use` // app-store-copy-ok: web only (canMentionBilling)
      : undefined,
    lines: [
      ...fresh.map(invite => ({
        icon: 'user' as const,
        text: `${[invite.firstName, invite.lastName].filter(Boolean).join(' ')} · ${invite.email} · ${invite.roleName}`,
      })),
      ...textLines(skipped),
    ],
    confirmLabel: fresh.length === 1 ? 'Send invite' : 'Send invites',
  });
  if (!approved) return declined();

  deps.onActivity?.('Sending invites');
  const invited: string[] = [];
  const notInvited: string[] = [];
  let seatsFull = false;
  try {
    for (const invite of fresh) {
      if (seatsFull || !(await SubscriptionService.postApiAppSubscriptionCheckLimit('users', 1))) {
        seatsFull = true;
        notInvited.push(invite.email);
        continue;
      }
      try {
        await UserService.postApiAppUserCreateInvite(invite);
        invited.push(invite.email);
      } catch (err) {
        notInvited.push(`${invite.email} (${errorMessage(err)})`);
      }
    }
  } finally {
    if (invited.length > 0) notifySetupChanged('team');
  }
  // app-store-copy-ok: web only (canMentionBilling)
  const linked = seatsFull && deps.env.canMentionBilling && showSetupLink(deps, 'team', 'Add a seat to invite the rest');
  return {
    saved: notInvited.length === 0,
    invited,
    ...(notInvited.length > 0 ? { notInvited } : {}),
    ...(skipped.length > 0 ? { skipped } : {}),
    note: notInvited.length > 0
      ? `Say who got an invite and who didn't, and why${seatsFull ? ` (the team is full${linked ? '; the card opens the team page' : ''})` : ''}.`
      : 'Confirm in one short sentence: they get an email to join.',
  };
}

async function execUpdateTeamMember(input: Input, deps: RoutyToolDeps) {
  if (!deps.env.canEditCompanySettings) return permissionDenied('change team members');
  const action = input.action;
  const person = cleanText(input.person);
  if (!person) return rejected(['Say who: their name or email.']);

  if (action === 'resend_invite' || action === 'cancel_invite') {
    deps.onActivity?.('Finding the invite');
    const pending = await loadPendingInvites();
    const key = labelKey(person);
    const matches = pending.filter(invite => labelKey(invite.email) === key || labelKey(inviteName(invite)) === key);
    if (matches.length !== 1) {
      return rejected([matches.length === 0
        ? `No pending invite for "${person}".`
        : `"${person}" matches ${matches.map(invite => invite.email).join(', ')}. Say which email.`]);
    }
    const invite = matches[0];
    const cancel = action === 'cancel_invite';
    const approved = await deps.requestConfirmation({
      title: cancel ? `Cancel ${inviteName(invite)}'s invite?` : `Resend ${inviteName(invite)}'s invite?`,
      lines: [{ icon: 'user', text: `${invite.email} · ${invite.roleName || 'Team Member'}` }],
      confirmLabel: cancel ? 'Cancel invite' : 'Resend invite',
      tone: cancel ? 'danger' : 'default',
    });
    if (!approved) return declined();
    try {
      if (cancel) await UserService.postApiAppUserCancelInvite(invite.id!);
      else await UserService.postApiAppUserResendInvite(invite.id!);
    } finally {
      notifySetupChanged('team');
    }
    return { saved: true, note: 'Confirm in one short sentence.' };
  }

  if (action !== 'change_role') return rejected(['action must be change_role, resend_invite or cancel_invite.']);
  const role = matchRoleName(input.role);
  if (!role) return rejected([`role must be one of ${SYSTEM_ROLE_NAMES.join(', ')}.`]);
  deps.onActivity?.('Checking your team');
  const [members, usage] = await Promise.all([loadTeamMembers(), SubscriptionService.getApiAppSubscriptionGetUsage()]);
  const match = matchTeamMember(members, person);
  if ('error' in match) return rejected([match.error]);
  const member = match.member;
  if (member.id === deps.env.currentUserId) return rejected(['Nobody can change their own role; another Super User has to.']);
  const current = primaryRoleName(member.roleNames);
  if (current === role) return { saved: false, note: `${member.name} is already a ${role}.` };
  const superUsers = members.filter(m => m.isActive !== false && primaryRoleName(m.roleNames) === 'Super User');
  if (current === 'Super User' && superUsers.length <= 1) return rejected(['The team needs at least one Super User.']);
  if (!isRoleAllowedOnPlan({
    tier: deps.env.planTier ?? null,
    additionalSeats: usage.additionalSeats ?? 0,
    members,
    roleName: role,
    userId: member.id,
  })) {
    return rejected([], [deps.env.canMentionBilling // app-store-copy-ok: web only (canMentionBilling)
      ? `${STARTER_TEAM_MEMBER_ONLY_MESSAGE} Add a seat or upgrade to Pro.`
      : STARTER_ROLE_RULE]);
  }

  const downgrade = isRoleDowngrade(current, role);
  const approved = await deps.requestConfirmation({
    title: `Make ${member.name || member.email} a ${role}?`,
    lines: role === 'Scheduling Assistant'
      ? [{ text: 'Scheduling Assistants book for others but don\'t take appointments themselves.' }]
      : [],
    changes: [{ label: 'Role', from: current ?? ((member.roleNames ?? []).join(', ') || 'None'), to: role }],
    confirmLabel: 'Change role',
    tone: downgrade ? 'danger' : 'default',
  });
  if (!approved) return declined();
  try {
    await UserService.putApiIdentityUsersRoles(member.id!, { roleNames: [role] });
  } finally {
    notifySetupChanged('team');
  }
  return { saved: true, note: 'Confirm in one short sentence.' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cards
// ─────────────────────────────────────────────────────────────────────────────

function execPresentChoices(input: Input, deps: RoutyToolDeps) {
  const prompt = cleanText(input.prompt);
  const options = (Array.isArray(input.options) ? input.options : [])
    .map(raw => (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>)
    .map(option => ({ label: cleanText(option.label), description: cleanText(option.description) || undefined }))
    .filter(option => option.label);
  const unique = options.filter((option, index) => options.findIndex(o => labelKey(o.label) === labelKey(option.label)) === index);
  if (!prompt || unique.length < 2 || unique.length > 8) {
    return { shown: false, note: 'present_choices needs a prompt and 2-8 distinct options, each with a label.' };
  }
  deps.onArtifact?.({
    type: 'choices',
    prompt,
    options: unique.map((option, index) => ({ id: `choice-${index + 1}`, ...option })),
    allowMultiple: input.allowMultiple === true,
    purpose: cleanText(input.purpose) || undefined,
  });
  return {
    shown: true,
    note:
      'The choices are on screen. Your reply is ONE short line introducing them (don\'t repeat the options), then ' +
      'STOP and wait. Their pick arrives as the next message.',
  };
}

function execOpenSetupPage(input: Input, deps: RoutyToolDeps) {
  const page = SETUP_PAGES.find(key => key === input.page);
  if (!page) return { shown: false, note: `page must be one of: ${SETUP_PAGES.join(', ')}.` };
  const bookingFormId = cleanText(input.bookingFormId) || undefined;
  if (page === 'form_editor' && !bookingFormId) {
    return { shown: false, note: 'form_editor needs bookingFormId (from list_booking_forms).' };
  }
  if (!showSetupLink(deps, page, cleanText(input.reason) || undefined, bookingFormId)) {
    return {
      shown: false,
      note:
        'That page isn\'t available in this app. Say in one sentence that the account administrator manages what ' +
        'the account includes, and don\'t point them anywhere else for it.',
    };
  }
  return {
    shown: true,
    note: 'The button is on screen. Say in one sentence what to do there. Never claim you opened it.',
  };
}

/** Execute a setup tool by name; errors propagate to the agent loop like `executeRoutyTool`. */
export async function executeSetupTool(name: string, input: Input, deps: RoutyToolDeps): Promise<unknown> {
  const tool = SETUP_TOOLS.find(t => t.name === name);
  if (!tool) throw new Error(`Unknown setup tool: ${name}`);
  const extra = unknownKeys(input, Object.keys((tool.input_schema.properties ?? {}) as Record<string, unknown>));
  if (extra.length > 0) return rejected([unknownKeysError(extra)]);
  switch (name) {
    case 'get_setup_overview':
      return execGetSetupOverview(deps);
    case 'get_booking_form':
      return execGetBookingForm(input, deps);
    case 'list_form_templates':
      return execListFormTemplates(input);
    case 'create_booking_form':
      return execCreateBookingForm(input, deps);
    case 'update_booking_form':
      return execUpdateBookingForm(input, deps);
    case 'edit_form_questions':
      return execEditFormQuestions(input, deps);
    case 'update_business_profile':
      return execUpdateBusinessProfile(input, deps);
    case 'update_company_hours':
      return execUpdateCompanyHours(input, deps);
    case 'update_company_settings':
      return execUpdateCompanySettings(input, deps);
    case 'save_address':
      return execSaveAddress(input, deps);
    case 'update_weekly_availability':
      return execUpdateWeeklyAvailability(input, deps);
    case 'invite_team_members':
      return execInviteTeamMembers(input, deps);
    case 'update_team_member':
      return execUpdateTeamMember(input, deps);
    case 'present_choices':
      return execPresentChoices(input, deps);
    case 'open_setup_page':
      return execOpenSetupPage(input, deps);
    default:
      throw new Error(`No handler for setup tool: ${name}`);
  }
}
